import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

export function Features() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const categories = [
    {
      id: 'infrastructure',
      label: 'Infraestructura',
      eyebrow: 'Base operativa',
      summary: 'Espacios y condiciones tecnicas para manufactura, almacenamiento y distribucion a escala.',
      metrics: ['12m altura libre', 'Pisos de alto trafico'],
      features: [
        {
          title: 'Naves Industriales',
          description: 'Espacios flexibles para produccion, almacenamiento y distribucion con configuracion adaptable.',
          highlights: ['Alturas libres', 'Pisos reforzados', 'Iluminacion LED'],
        },
        {
          title: 'Zona Franca',
          description: 'Entorno preparado para operaciones con beneficios aduaneros y fiscales en un marco formal.',
          highlights: ['Exencion de impuestos', 'Tramites simplificados', 'Certificacion ISO'],
        },
      ],
    },
    {
      id: 'services',
      label: 'Servicios',
      eyebrow: 'Continuidad tecnica',
      summary: 'Servicios esenciales para sostener operaciones criticas con tiempos de respuesta previsibles.',
      metrics: ['Energia de respaldo', 'Conectividad redundante'],
      features: [
        {
          title: 'Energia Confiable',
          description: 'Sistema electrico propio y respaldo disenado para proteger la continuidad operativa.',
          highlights: ['Planta propia', 'Respaldo UPS', 'Generadores'],
        },
        {
          title: 'Conectividad',
          description: 'Infraestructura digital lista para operaciones sensibles, monitoreo y coordinacion remota.',
          highlights: ['Fibra optica', 'WiFi industrial', 'Alta disponibilidad'],
        },
      ],
    },
    {
      id: 'security',
      label: 'Seguridad',
      eyebrow: 'Control del recinto',
      summary: 'Capas de proteccion fisica y control de accesos para operar con confianza todos los dias.',
      metrics: ['Cobertura 24/7', 'Accesos monitoreados'],
      features: [
        {
          title: 'Seguridad 24/7',
          description: 'Control de accesos, CCTV y vigilancia permanente para proteger personas, carga y activos.',
          highlights: ['Guardia 24/7', 'CCTV HD', 'Control biometrico'],
        },
        {
          title: 'Logistica Avanzada',
          description: 'Muelle, maniobra y accesos pensados para alto flujo de carga con operacion ordenada.',
          highlights: ['Muelles niveladores', 'Patios amplios', 'Acceso directo'],
        },
      ],
    },
  ];

  return (
    <section id="features" className="py-20 md:py-32 bg-slate-50 border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(15,39,65,0.08),transparent_40%)]" />

          <div className="relative px-6 py-10 md:px-10 md:py-12 lg:px-14">
            <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
              <div className="max-w-3xl">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-slate-100 border border-slate-200 text-slate-700 mb-6 shadow-sm">
                  Caracteristicas
                </div>

                <h2 className="text-4xl md:text-5xl lg:text-6xl text-slate-950 tracking-tight leading-[1.05] mb-5">
                  Infraestructura con
                  <span className="block text-slate-600 font-medium">estandar corporativo y control operativo</span>
                </h2>

                <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                  Capacidades clave estructuradas por frentes para facilitar lectura ejecutiva y toma de decision.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  { value: '24/7', label: 'Operacion monitoreada' },
                  { value: '3', label: 'Frentes estrategicos' },
                  { value: 'ISO', label: 'Marco de calidad' },
                  { value: 'B2B', label: 'Enfoque empresarial' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 18 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.1 + index * 0.08 }}
                    className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5"
                  >
                    <div className="text-2xl md:text-3xl font-semibold text-slate-950">{stat.value}</div>
                    <div className="text-sm text-slate-500 mt-2">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="mt-12"
            >
              <Tabs defaultValue="infrastructure" className="w-full" role="region" aria-label="Características del parque">
                <TabsList className="h-auto grid w-full grid-cols-1 gap-3 rounded-[1.6rem] border border-slate-200 bg-slate-100/90 p-2 shadow-inner md:grid-cols-3 md:gap-2" role="tablist">
                  {categories.map((category) => (
                    <TabsTrigger
                      key={category.id}
                      value={category.id}
                      role="tab"
                      aria-controls={`panel-${category.id}`}
                      className="h-auto min-h-[78px] rounded-[1.1rem] px-5 py-4 text-left text-sm md:text-base data-[state=active]:bg-slate-950 data-[state=active]:text-white data-[state=active]:shadow-lg text-slate-600 transition-all"
                    >
                      <div className="w-full">
                        <div className="text-[11px] uppercase tracking-[0.24em] opacity-60 leading-none mb-2">{category.eyebrow}</div>
                        <div className="font-semibold tracking-tight">{category.label}</div>
                      </div>
                    </TabsTrigger>
                  ))}
                </TabsList>

                {categories.map((category) => (
                  <TabsContent key={category.id} value={category.id} className="mt-6" role="tabpanel" id={`panel-${category.id}`}>
                    <div className="grid gap-6 xl:grid-cols-[0.72fr_1.28fr]">
                      <div className="rounded-[1.75rem] bg-slate-950 p-8 text-white shadow-[0_24px_60px_-36px_rgba(15,23,42,0.7)]">
                        <div className="text-[11px] uppercase tracking-[0.24em] text-slate-400 mb-4">{category.eyebrow}</div>
                        <h3 className="text-3xl md:text-[2rem] font-semibold tracking-tight mb-4">{category.label}</h3>
                        <p className="text-slate-300 leading-relaxed">{category.summary}</p>

                        <div className="mt-8 space-y-3">
                          {category.metrics.map((metric) => (
                            <div
                              key={metric}
                              className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200"
                            >
                              {metric}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid gap-6 md:grid-cols-2">
                        {category.features.map((feature, index) => (
                          <motion.article
                            key={feature.title}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * index }}
                            className="group relative h-full"
                          >
                            <div className="h-full rounded-[1.75rem] border border-slate-200 bg-white px-7 pb-7 pt-8 shadow-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_24px_50px_-32px_rgba(15,23,42,0.4)]">
                              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">Capacidad</div>
                              <h4 className="text-2xl text-slate-950 mb-3 font-semibold tracking-tight">{feature.title}</h4>
                              <p className="text-slate-600 leading-relaxed">{feature.description}</p>

                              <div className="mt-8 grid gap-3">
                                {feature.highlights.map((highlight) => (
                                  <div
                                    key={highlight}
                                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
                                  >
                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
                                    <span>{highlight}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </motion.article>
                        ))}
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
